import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { store } from './store/store';
import theme from './theme';

import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import PublisherRegister from './pages/auth/PublisherRegister';
import ForgotPassword from './pages/auth/ForgotPassword';
import ChangePassword from './pages/auth/ChangePassword';
import Profile from './pages/Profile';

import AdminDashboard from './pages/admin/Dashboard';
import AdminPublishers from './pages/admin/Publishers';
import AdminUsers from './pages/admin/Users';
import AdminBooks from './pages/admin/Books';
import AdminBookRequests from './pages/admin/BookRequests';
import AdminBookRequestBooks from './pages/admin/BookRequestBooks';
import AdminUploadHistory from './pages/admin/UploadHistory';
import AdminUpload from './pages/admin/AdminUpload';
import AdminPublisherContent from './pages/admin/PublisherContent';

import PublisherDashboard from './pages/publisher/Dashboard';
import PublisherBooks from './pages/publisher/Books';
import UploadBooks from './pages/publisher/UploadBooks';
import PublisherUploadHistory from './pages/publisher/UploadHistory';
import Submissions from './pages/publisher/Submissions';
import Agreement from './pages/publisher/Agreement';

import UserDashboard from './pages/user/Dashboard';
import UserBooks from './pages/user/Books';
import SavedBooks from './pages/user/SavedBooks';
import BookDetail from './pages/user/BookDetail';

import { useAuth } from './hooks/useAuth';

function PrivateRoute({ children, roles }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) {
    if (user?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    if (user?.role === 'publisher') return <Navigate to="/publisher/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated) {
    if (user?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    if (user?.role === 'publisher') return <Navigate to="/publisher/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/register/publisher" element={<PublicRoute><PublisherRegister /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />

      {/* All authenticated */}
      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />

      {/* Admin */}
      <Route path="/admin/dashboard" element={<PrivateRoute roles={['admin']}><AdminDashboard /></PrivateRoute>} />
      <Route path="/admin/publishers" element={<PrivateRoute roles={['admin']}><AdminPublishers /></PrivateRoute>} />
      <Route path="/admin/publisher-content" element={<PrivateRoute roles={['admin']}><AdminPublisherContent /></PrivateRoute>} />
      <Route path="/admin/users" element={<PrivateRoute roles={['admin']}><AdminUsers /></PrivateRoute>} />
      <Route path="/admin/books" element={<PrivateRoute roles={['admin']}><AdminBooks /></PrivateRoute>} />
      <Route path="/admin/book-requests" element={<PrivateRoute roles={['admin']}><AdminBookRequests /></PrivateRoute>} />
      <Route path="/admin/book-requests/:id/books" element={<PrivateRoute roles={['admin']}><AdminBookRequestBooks /></PrivateRoute>} />
      <Route path="/admin/upload" element={<PrivateRoute roles={['admin']}><AdminUpload /></PrivateRoute>} />
      <Route path="/admin/upload-history" element={<PrivateRoute roles={['admin']}><AdminUploadHistory /></PrivateRoute>} />

      {/* Publisher */}
      <Route path="/publisher/dashboard" element={<PrivateRoute roles={['publisher']}><PublisherDashboard /></PrivateRoute>} />
      <Route path="/publisher/books" element={<PrivateRoute roles={['publisher']}><PublisherBooks /></PrivateRoute>} />
      <Route path="/publisher/books/add" element={<PrivateRoute roles={['publisher']}><PublisherBooks /></PrivateRoute>} />
      <Route path="/publisher/upload" element={<PrivateRoute roles={['publisher']}><UploadBooks /></PrivateRoute>} />
      <Route path="/publisher/upload-history" element={<PrivateRoute roles={['publisher']}><PublisherUploadHistory /></PrivateRoute>} />
      <Route path="/publisher/submissions" element={<PrivateRoute roles={['publisher']}><Submissions /></PrivateRoute>} />
      <Route path="/publisher/agreement" element={<PrivateRoute roles={['publisher']}><Agreement /></PrivateRoute>} />

      {/* End User */}
      <Route path="/dashboard" element={<PrivateRoute roles={['end_user']}><UserDashboard /></PrivateRoute>} />
      <Route path="/books" element={<PrivateRoute roles={['end_user', 'admin', 'publisher']}><UserBooks /></PrivateRoute>} />
      <Route path="/saved-books" element={<PrivateRoute roles={['end_user']}><SavedBooks /></PrivateRoute>} />
      <Route path="/books/:id" element={<PrivateRoute><BookDetail /></PrivateRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
}
